import React, { Fragment } from 'react';
import { GQLJobState, GQLParty } from '@jewelutions/core-crm-server-api-client-domain';
import { SectionCard, DetailsList } from '../../../../components';
import { MerchantNavigation } from '../../navigation/MerchantNavigation';
import { useRouteMatch } from 'react-router-dom';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { LoadingSpinner } from '../../../../components';
import { Button, Card, Grid, Typography } from '@material-ui/core';
import { Play } from 'react-feather';
import { successNotification } from '../../../../core/utils';
import { toast } from 'react-toastify';

interface IProps {
  party: GQLParty;
  merchantChannelId: string;
}

const MerchantChannelCatalogDetails: React.FC<IProps> = ({ party, merchantChannelId }: IProps) => {
  const { merchantChannelCatalogId } = MerchantNavigation.merchantChannelCatalogDetails.paramsFromPath(
    useRouteMatch()
  );

  const [scheduleWebshopPublishing] = GQLOperationBuilder.webshop.scheduleWebshopPublishing();

  const {
    loading: catalogLoading,
    data: catalogData,
    refetch: catalogRefetch
  } = GQLOperationBuilder.merchantChannelCatalog.getMerchantChannelCatalog({
    merchantChannelCatalogId: merchantChannelCatalogId
  });

  const {
    loading: merchantChannelSyncStatsLoading,
    data: merchantChannelSyncStatsData,
    refetch: merchantChannelSyncStatsRefetch
  } = GQLOperationBuilder.webshop.getMerchantChannelSynchronisedWithShopStatus({
    merchantChannelId: merchantChannelId
  });

  if (catalogLoading || merchantChannelSyncStatsLoading) return <LoadingSpinner />;
  const catalog = GQLOperationBuilder.merchantChannelCatalog.merchantChannelCatalogFromResponse(catalogData);
  const merchantChannelSyncStats = GQLOperationBuilder.webshop.merchantChannelSynchronisedWithShopStatusFromResponse(
    merchantChannelSyncStatsData
  );

  const reloadData = async () => {
    catalogRefetch();
    merchantChannelSyncStatsRefetch();
  };

  const forceScheduleWebshopPublishing = () => {
    const mutationVariables = { merchantChannelId: catalog.channelId };
    scheduleWebshopPublishing({ variables: mutationVariables }).then(_result => {
      successNotification(toast, 'New merchant catalog publishing scheduled', reloadData());
    });
  };

  return (
    <Fragment>
      <Grid item style={{ marginTop: '8px' }}>
        <SectionCard title="Catalog Details">
          <DetailsList
            list={[
              {
                primary: 'ID',
                secondary: catalog.id
              },
              {
                primary: 'Product collections',
                secondary: catalog.availableCollections.length
              },
              {
                primary: 'Created At',
                secondary: new Date(Date.parse(catalog.createdAt)).toLocaleString()
              },
              {
                primary: 'Updated At',
                secondary: new Date(Date.parse(catalog.updatedAt)).toLocaleString()
              }
            ]}
          />
        </SectionCard>
      </Grid>
      <Grid container direction="row">
        <Grid item md={4} style={{ marginBottom: '8px' }}>
          <Card style={{ padding: '8px' }}>
            {merchantChannelSyncStats.lastSynchronisationJob &&
              [GQLJobState.Created, GQLJobState.Started].includes(
                merchantChannelSyncStats.lastSynchronisationJob!.state
              ) && (
                <Typography>
                  Catalog already scheduled for publishing. Publishing job created at{' '}
                  {merchantChannelSyncStats.lastSynchronisationJob.createdAt} with state{' '}
                  {merchantChannelSyncStats.lastSynchronisationJob.state}. Will be executed soon.
                </Typography>
              )}
            {merchantChannelSyncStats.lastSynchronisationJob &&
              [GQLJobState.FinishedSuccess].includes(
                merchantChannelSyncStats.lastSynchronisationJob!.state
              ) && (
                <Typography>
                  Catalog last published to shop at{' '}
                  {merchantChannelSyncStats.lastSynchronisationJob.createdAt} succesfully.
                </Typography>
              )}
            {merchantChannelSyncStats.lastSynchronisationJob &&
              [GQLJobState.FinishedFailed].includes(
                merchantChannelSyncStats.lastSynchronisationJob!.state
              ) && (
                <Typography>
                  Catalog last published to shop at{' '}
                  {merchantChannelSyncStats.lastSynchronisationJob.createdAt} but failed. Try to publish
                  again.
                </Typography>
              )}
            {merchantChannelSyncStats.lastSynchronisationJob &&
              [GQLJobState.Started].includes(merchantChannelSyncStats.lastSynchronisationJob!.state) && (
                <Typography>
                  Catalog scheduled for publishing to shop at{' '}
                  {merchantChannelSyncStats.lastSynchronisationJob.createdAt} and is currently in progress.
                </Typography>
              )}

            <Button
              disabled={
                merchantChannelSyncStats.lastSynchronisationJob &&
                [GQLJobState.Started, GQLJobState.Created].includes(
                  merchantChannelSyncStats.lastSynchronisationJob!.state
                )
                  ? true
                  : false
              }
              variant="contained"
              color="primary"
              style={{ width: '50%' }}
              onClick={() => {
                forceScheduleWebshopPublishing();
              }}
            >
              <Play />
              <span style={{ marginLeft: '4px' }}>Publish now</span>
            </Button>
          </Card>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default MerchantChannelCatalogDetails;
